import Titulo from '../../components/tipografia/Titulo';
import React, { useCallback, useState, useEffect } from 'react';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import VBox from '../../components/layout/VBox';
import Icon from '../../components/Icon';
import { mdiArrowLeft, mdiLoading, mdiMagnify, mdiPlus } from '@mdi/js';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import axios from 'axios';
import { useDebounce } from '../chat/useDebounce';
import { toast } from 'react-toastify';
import Grid, { GridItem } from '../../components/layout/Grid';
import { parseISO, format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import Badge from '../../components/layout/Badge';
import CampoTexto from '@/components/Formulario/CampoTexto';

type ResponseList = {
    status: number;
    ok?: boolean;
    result: any[];
    total: number;
    cursor: string | number | null;
};

type Props = {};

const MensagensCampanhaPage = ({ }: Props) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [search, setSearch] = React.useState<string>('');
    const debouncedSearch = useDebounce(search, 500);

    // Consulta para obter os detalhes da campanha
    const campanhaQuery = useQuery(
        ['campanha_mensagem', id],
        async () => {
            const res = await axios.get(`/api/campanha_mensagem/${id}`);
            return res.data;
        },
        {
            enabled: !!id,
            staleTime: 30000,
        }
    );

    // Consulta para obter as mensagens da campanha
    const queryList = useInfiniteQuery({
        queryKey: ['campanha_mensagem_mensagens', id, debouncedSearch],
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams({
                txt: debouncedSearch,
                c: pageParam,
            });

            return await axios.get<ResponseList>(
                `/api/campanha_mensagem/${id}/mensagens?${params.toString()}`,
                {
                    withCredentials: true,
                },
            );
        },
        enabled: !!id,
        getNextPageParam: lastPage => lastPage.data.cursor ?? undefined,
    });

    // Renderizador de status
    const renderStatus = (estado: string, descricao?: string) => {
        if (estado === 'convertido') {
            return <Badge variant="success">Convertido</Badge>;
        }
        if (estado === 'concluido') {
            if (!descricao) {
                return <Badge variant="info">Enviado</Badge>;
            } else {
                return <Badge variant="warning">Não enviado</Badge>;
            }
        }
        if (estado === 'erro') {
            return <Badge variant="danger">Erro</Badge>;
        }
        return <Badge variant="info">Pendente</Badge>;
    };

    const headers: DataTableHeader[] = [
        { label: 'ID', width: '8%', column: 'id' },
        {
            label: 'ID do cliente no canal',
            width: '12%',
            column: 'canal_cliente_id',
        },
        {
            label: 'Dados',
            width: '30%',
            render: (row: any) => {
                return row.metadados ? <div>{Object.entries(row.metadados).map(([k, v]: any) => <span style={{ marginRight: '0.5rem', color: '#555' }}><span style={{ fontWeight: 'bold' }}>{k}</span>: {v}</span>)}</div> : <p>(Sem dados)</p>
            },
        },
        {
            label: 'Status',
            width: '10%',
            render: (row: any) => renderStatus(row.estado, row.descricao),
        },
        {
            label: 'Data de registro',
            width: '15%',
            render: (row: any) => (
                <span>
                    {row.data_criacao
                        ? format(parseISO(row.data_criacao), 'dd/MM/yyyy HH:mm')
                        : '-'}
                </span>
            ),
        }, {
            label: 'Data de processamento',
            width: '15%',
            render: (row: any) => (
                <span>
                    {row.data_conclusao
                        ? format(parseISO(row.data_conclusao), 'dd/MM/yyyy HH:mm')
                        : '-'}
                </span>
            ),
        },
        {
            label: 'Descrição',
            width: '10%',
            render: (row: any) => (
                <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.descricao || null}>
                    {row.descricao || '-'}
                </div>
            ),
        }
    ];

    const queryFlatList =
        queryList.data?.pages?.map(p => p.data.result)?.flat() ?? [];
    const total =
        queryList.data?.pages?.[queryList.data.pages.length - 1]?.data?.total ?? 0;

    const campanha = campanhaQuery.data?.result;

    return (
        <LayoutUsuario>
            <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
                {/* Header */}
                <Grid>
                    <GridItem sm={12} md={8}>
                        <HBox gap="12px" style={{ alignItems: 'center' }}>
                            <Botao
                                variant="none-primary"
                                icon={mdiArrowLeft}
                                onClick={() => navigate('/campanha_mensagem')}
                            />
                            <VBox style={{ flex: 1 }}>
                                <Titulo>Mensagens da Campanha #{id}</Titulo>
                                <Descricao>
                                    Visualize as mensagens enviadas na campanha
                                </Descricao>
                            </VBox>
                        </HBox>
                    </GridItem>
                    <GridItem sm={12} md={4}>
                        <HBox gap="12px" className={styles.headerAcoes} style={{ flex: 1 }}>
                            {/* Botões adicionais podem ser colocados aqui */}
                        </HBox>
                    </GridItem>
                </Grid>

                {/* Dados da Campanha */}
                {campanhaQuery.isLoading ? (
                    <div>
                        Carregando dados da campanha...{' '}
                        <Icon
                            path={mdiLoading}
                            size={0.8}
                            color="var(--tc-color-gray-700)"
                            spin
                        />
                    </div>
                ) : campanha ? (
                    <VBox style={{ width: '100%', height: 'auto' }}>
                        <HBox className={styles.campanhaInfo}>
                            <div className={styles.infoItem}>
                                <strong>Canal:</strong> {campanha.canal?.nome || '-'}
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Estado:</strong> {campanha.estado || '-'}
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Data de Criação:</strong>{' '}
                                {campanha.data_criacao
                                    ? format(parseISO(campanha.data_criacao), 'dd/MM/yyyy HH:mm')
                                    : '-'}
                            </div>
                        </HBox>
                        <HBox className={styles.campanhaInfo}>
                            <div className={styles.infoItem}>
                                <strong>Modelo da mensagem:</strong> {campanha.mensagem_programada?.conteudo || '-'}
                            </div>
                        </HBox>
                    </VBox>
                ) : (
                    <div>Campanha não encontrada ou sem informações disponíveis.</div>
                )}

                {/* Pesquisa */}
                <Grid>
                    <GridItem sm={12}>
                        <CampoTexto
                            prepend={
                                <Icon
                                    path={mdiMagnify}
                                    size={1}
                                    color="var(--tc-color-gray-500)"
                                />
                            }
                            placeholder="Pesquisar"
                            value={search}
                            onChange={setSearch}
                        />
                    </GridItem>
                </Grid>

                {/* Status da consulta */}
                {queryList.isFetching && (
                    <div>
                        Aguarde...{' '}
                        <Icon
                            path={mdiLoading}
                            size={0.8}
                            color="var(--tc-color-gray-700)"
                            spin
                        />
                    </div>
                )}
                {!queryList.isFetching && (
                    <div>
                        Exibindo {queryFlatList?.length ?? '0'} de {total ?? '0'}{' '}
                        resultados.
                    </div>
                )}

                {/* Tabela de Mensagens */}
                <DataTable
                    isLoading={queryList.isFetching}
                    cabecalhos={headers}
                    linhas={queryFlatList ?? []}
                    hasMoreData={queryList.hasNextPage}
                    onLoadMore={async () => queryList.fetchNextPage()}
                />
            </VBox>
        </LayoutUsuario>
    );
};

export default MensagensCampanhaPage;
